import { useMemo, useState } from "react";
import './CrossSelling.css'; // Importiere die CSS-Datei für die Stile

export type CrossSellCategory =
  | "treppenlift"
  | "badumbau"
  | "pflegehilfsmittel"
  | "hausnotruf"
  | "pflegebett"
  | "sonstiges";

export type CrossSellRelevance = "sofort" | "4w" | "spaeter";

export type CrossSellPayload = {
  leadId: string;
  clientName: string;
  clientPhone?: string | null;
  clientEmail?: string | null;
  category: CrossSellCategory;
  subCategory?: string | null;
  note?: string;
  relevance: CrossSellRelevance;
  consentGranted: boolean;
  // Additional fields for Badumbau details
  badumbauDetails?: {
    pflegegrad?: string;
    wohnsituation?: string;
    einverstaendnisVermieter?: string;
    jetzigeBadausstattung?: string;
    budgetrahmen?: string;
    restschwelle?: string;
    vorOrtTermin?: string;
    bedarf?: string;
    umbaumassnahmen?: string;
    nutzung?: string;
    system?: string;
    koerpergewicht?: string;
    rezept?: string;
    krankenkasse?: string;
  };
};

// ---- Statische Defaults (integriert in die Komponente) ----
const DEFAULTS = {
  leadId: "LEAD-12345",
  clientName: "Max Mustermann",
  clientPhone: "0176 2323123",
  clientEmail: "max.mustermann@gmail.com",
};

// 6 Hauptkategorien (Kacheln)
const CATEGORY_OPTIONS: { value: CrossSellCategory; label: string; emoji: string }[] = [
  { value: "treppenlift", label: "Treppen-/Sitzlift", emoji: "🪜" },
  { value: "badumbau", label: "Badumbau", emoji: "🛁" },
  { value: "pflegehilfsmittel", label: "Pflegehilfsmittel", emoji: "🎒" },
  { value: "hausnotruf", label: "Hausnotruf", emoji: "📟" },
  { value: "pflegebett", label: "Pflegebett", emoji: "🛏️" },
  { value: "sonstiges", label: "Sonstiges", emoji: "✨" },
];

// Detail-Kacheln je Kategorie (Stufe 2)
const SUBCATEGORY_MAP: Record<CrossSellCategory, { label: string; value: string, icon: string }[]> = {
  treppenlift: [
    { label: "Sitzlift (innen)", value: "sitzlift_innen", icon: "🪜🏠" },
    { label: "Sitzlift (außen)", value: "sitzlift_aussen", icon: "🪜🌳" },
    { label: "Plattformlift", value: "plattformlift", icon: "🪜🦽" },
  ],
  badumbau: [
    { label: "Badewanne zur Dusche", value: "badewanne_zur_dusche", icon: "🛁➡️🚿" },
    { label: "Badsanierung", value: "badsanierung", icon: "🛁➡️🛁" },
    { label: "Badewannentür", value: "badewannentuer", icon: "🚪🛁" },
    { label: "Seniorenbadewanne", value: "seniorenbadewanne", icon: "🛁👵" },
    { label: "Badewannenlift", value: "badewannenlift", icon: "🛁⬆️" },
  ],
  pflegehilfsmittel: [
    { label: "Monatspauschale (40€)", value: "pauschale_40", icon: "🎒40€" },
    { label: "Rollator", value: "rollator", icon: "🎒🦽" },
    { label: "Toilettenstuhl", value: "toilettenstuhl", icon: "🎒🚽" },
  ],
  hausnotruf: [
    { label: "Basis (Kabel)", value: "basis", icon: "📟📞" },
    { label: "Mobil (GPS)", value: "mobil", icon: "📟📱" },
    { label: "Mit Sturzsensor", value: "sturzsensor", icon: "📟🤸" },
  ],
  pflegebett: [
    { label: "Standardbett", value: "standard", icon: "🛏️" },
    { label: "Niedrigbett", value: "niedrigbett", icon: "🛏️⬇️" },
    { label: "Zubehör (Matratze, Seitengitter)", value: "zubehoer", icon: "🛏️➕" },
  ],
  sonstiges: [{ label: "Individuell beschreiben", value: "custom", icon: "✨📝" }],
};

// Relevanz (Stufe 3 – als Kacheln)
const RELEVANCE_OPTIONS: { value: CrossSellRelevance; label: string; hint: string; badge: string }[] = [
  { value: "sofort", label: "Sofort", hint: "Akuter Bedarf, Rückruf zeitnah", badge: "🔥" },
  { value: "4w", label: "In 4 Wochen", hint: "Planbar, Termin in Kürze", badge: "⏳" },
  { value: "spaeter", label: "Später / unklar", hint: "Unverbindlich, Info erwünscht", badge: "🗓️" },
];

// Provisionslogik (gamified)
const COMMISSION_BASE: Record<CrossSellCategory, number> = {
  treppenlift: 20,
  badumbau: 40,
  pflegehilfsmittel: 10,
  hausnotruf: 10,
  pflegebett: 20,
  sonstiges: 10,
};
const COMMISSION_DETAIL_BONUS: Record<string, number> = {
  // Treppenlift
  sitzlift_innen: 60,
  sitzlift_aussen: 80,
  plattformlift: 120,
  // Badumbau
  badewanne_zur_dusche: 10,
  badsanierung: 30,
  badewannentuer: 10,
  seniorenbadewanne: 10,
  badewannenlift: 20,
  // Pflegehilfsmittel
  pauschale_40: 20,
  rollator: 15,
  toilettenstuhl: 15,
  // Hausnotruf
  basis: 5,
  mobil: 8,
  sturzsensor: 10,
  // Pflegebett
  standard: 30,
  niedrigbett: 40,
  zubehoer: 15,
  // Sonstiges
  custom: 0,
};
const COMMISSION_RELEVANCE_MULTIPLIER: Record<CrossSellRelevance, number> = {
  sofort: 1.2,
  "4w": 1.0,
  spaeter: 0.7,
};

// Zusatzinfos (optional)
const EXTRA_INFO_TILES: { value: string; label: string; icon: string }[] = [
  { value: "kasse_vorhanden", label: "Pflegekasse vorhanden", icon: "🏦" },
  { value: "pflegestufe", label: "Pflegegrad bekannt", icon: "🧾" },
  { value: "kontakt_betreuer", label: "Betreuer/Kontaktperson", icon: "👥" },
  { value: "wohnform", label: "Wohnform geprüft", icon: "🏠" },
];

// ---- Helpers ----
function formatCurrency(amount: number) {
  return amount.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}
function calcCommission(category: CrossSellCategory, subCategory?: string | null, relevance?: CrossSellRelevance) {
  const base = COMMISSION_BASE[category] ?? 0;
  const detail = subCategory ? (COMMISSION_DETAIL_BONUS[subCategory] ?? 0) : 0;
  const multi = relevance ? (COMMISSION_RELEVANCE_MULTIPLIER[relevance] ?? 1) : 1;
  const raw = (base + detail) * multi;
  return Math.max(0, Math.round(raw / 10) * 10); // runden (gamified)
}

// ---- UI Atoms ----
function Tile({
  title,
  subtitle,
  active,
  onClick,
  emoji,
  disabled,
}: {
  title: string;
  subtitle?: string;
  emoji?: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="cs-tile"
      aria-pressed={!!active}
      style={{
        width: "100%",
        textAlign: "left",
        borderRadius: 14,
        border: active ? "2px solid #4f46e5" : "1px solid #d1d5db",
        padding: "18px 16px",
        background: disabled ? "#f3f4f6" : active ? "#eef2ff" : "#fff",
        boxShadow: active ? "0 4px 14px rgba(79,70,229,0.18)" : "0 2px 8px rgba(0,0,0,0.06)",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {emoji && <span style={{ fontSize: 28 }}>{emoji}</span>}
        <div style={{ display: "grid" }}>
          <span style={{ fontWeight: 500, fontSize: 16 }}>{title}</span>
          {subtitle && <span style={{ color: "#6b7280", fontSize: 14 }}>{subtitle}</span>}
        </div>
      </div>
    </button>
  );
}

// Modern Select Tile Component
function ModernSelectTile({
  label,
  value,
  onChange,
  options,
  icon,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; score: string; icon: string }[];
  icon: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
        {icon}
        {label}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            style={{
              padding: "12px 8px",
              borderRadius: 12,
              border: value === option.value ? "2px solid #10b981" : "1px solid #e5e7eb",
              background: value === option.value ? "#ecfdf5" : "#ffffff",
              boxShadow: value === option.value ? "0 4px 12px rgba(16, 185, 129, 0.15)" : "0 1px 3px rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              textAlign: "center",
              minHeight: "80px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
            onMouseEnter={(e) => {
              if (value !== option.value) {
                e.currentTarget.style.background = "#f9fafb";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
              }
            }}
            onMouseLeave={(e) => {
              if (value !== option.value) {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
              }
            }}
          >
            <div style={{ fontSize: 20, marginBottom: 2 }}>{option.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", lineHeight: 1.2 }}>
              {option.label}
            </div>
            <div style={{ 
              fontSize: 10, 
              fontWeight: 700, 
              color: value === option.value ? "#059669" : "#6b7280",
              backgroundColor: value === option.value ? "#d1fae5" : "#f3f4f6",
              padding: "2px 6px",
              borderRadius: 8,
              marginTop: 2,
            }}>
              {option.score} Punkte
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Modern Input Tile Component
function ModernInputTile({
  label,
  value,
  onChange,
  placeholder,
  icon,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  icon: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
        {icon}
        {label}
      </div>
      <div style={{ position: "relative" }}>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "16px 16px 16px 48px",
            borderRadius: 12,
            border: "2px solid #e5e7eb",
            background: "#ffffff",
            fontSize: 16,
            fontWeight: 500,
            color: "#374151",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease",
            outline: "none",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#3b82f6";
            e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#e5e7eb";
            e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
          }}
        />
        <div style={{ 
          position: "absolute", 
          left: "16px", 
          top: "50%", 
          transform: "translateY(-50%)",
          color: "#6b7280",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {icon}
        </div>
        {value && (
          <div style={{ 
            position: "absolute", 
            right: "12px", 
            top: "50%", 
            transform: "translateY(-50%)",
            backgroundColor: "#dbeafe",
            color: "#1d4ed8",
            padding: "4px 8px",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
          }}>
            €{value}
          </div>
        )}
      </div>
    </div>
  );
}

function StickyCommissionBar({
  amount,
  onBack,
  onNext,
  onSubmit,
  canNext,
  canSubmit,
  submitting,
}: {
  amount: number;
  onBack?: () => void;
  onNext?: () => void;
  onSubmit?: () => void;
  canNext?: boolean;
  canSubmit?: boolean;
  submitting?: boolean;
}) {
  return (
    <div
      role="region"
      aria-label="Provision & Navigation"
      style={{
        position: "sticky",
        bottom: 0,
        left: 0,
        right: 0,
        borderTop: "1px solid #e5e7eb",
        background: "#fff",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        zIndex: 5,
        marginTop: 16,
        paddingTop: 16,
        borderRadius: "16px 16px 0 0",
      }}
    >
      <div>
        <div style={{ fontSize: 16, fontWeight: 700 }}>
          Potenzielle Provision: {formatCurrency(amount)}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Hinweis: Abhängig von Abschluss & Produktvariante.
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>

        {onSubmit && (
          <button
            type="button"
            className="btn btn-primary btn-medium-small"
            onClick={onSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Sende…" : "Abschließen"}
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Section-Komponente (ohne Props) ----
export {
    StickyCommissionBar,
    Tile,
    ModernSelectTile,
    ModernInputTile,
    CATEGORY_OPTIONS,
    SUBCATEGORY_MAP,
    RELEVANCE_OPTIONS,
    EXTRA_INFO_TILES,
    DEFAULTS,
    formatCurrency,
    calcCommission,

}
